import React, { useContext, useEffect, useRef, useState, useCallback, useMemo } from "react";
import { createPortal } from "react-dom";
import { TicketContext } from "../../../app/providers/TicketContext";
import { AuthContext } from "../../../app/providers/AuthContext";
import {
  FaFilePdf,
  FaFileVideo,
  FaFileImage,
  FaFileAlt,
  FaFileExcel,
  FaPaperclip,
  FaEllipsisV,
  FaCheckCircle,
  FaExclamationCircle,
  FaExpand,
  FaStar,
  FaRegStar,
  FaChevronDown,
  FaCog,
  FaDownload,
  FaExternalLinkAlt,
  FaTimes,
  FaInfoCircle,
  FaExclamationTriangle,
  FaTimesCircle,
  FaCircle,
  FaRegCircle,
  FaSun,
  FaMoon,
} from "react-icons/fa";
import * as XLSX from 'xlsx';
import useStarredEvents from "./useStarredEvents";
import "./LoadingBar.css";
import EventTable from "./EventTable";
import { BlinkBlur } from "react-loading-indicators";
import RPBodyBG from "../../../assets/logo/rpbody.png";
import "./RunIndex.css"
import FlowChart from "../../flowchart/FlowChart";
import { sanitizeHtml } from "../../../shared/security/sanitizeHtml";
import { safeParseJson } from "../../../shared/security/safeJson";
import TreeStructure from "./TreeStructure";
import { TicketWebSocketContext } from "../../websocket/TicketWebSocketProvider";
import { FaNetworkWired } from "react-icons/fa";
import { isEventDisabled } from "../../../shared/utils/eventUtils";

/* ─────────────────────────────────────────────────────────────────────────
   STYPE → görsel tema (Bootstrap'ten esinlenilmiş profesyonel renk paleti)
   Her preset:
     - icon:    React component
     - bg:      gövde zemin rengi (light bubble)
     - bgDark:  koyu varyant (right-aligned bubble)
     - border:  bordür / sol şerit
     - fg:      birincil yazı rengi
     - accent:  ikon + accent renkler
   Tailwind class kullanmıyoruz — purge'a takılmaması için inline rgba.
───────────────────────────────────────────────────────────────────────── */
const STYPE_PRESETS = {
  // INFO bilinçli olarak override yapmaz — default chat bubble görünümü kullanılır.
  SUCCESS: {
    icon: FaCheckCircle,
    label: 'Success',
    bg: '#ecfdf5', bgDark: '#064e3b', border: '#10b981',
    fg: '#065f46', fgDark: '#d1fae5', accent: '#059669',
    badgeBg: '#d1fae5', badgeFg: '#047857',
  },
  WARNING: {
    icon: FaExclamationTriangle,
    label: 'Warning',
    bg: '#fffbeb', bgDark: '#78350f', border: '#f59e0b',
    fg: '#92400e', fgDark: '#fef3c7', accent: '#d97706',
    badgeBg: '#fef3c7', badgeFg: '#b45309',
  },
  DANGER: {
    icon: FaTimesCircle,
    label: 'Danger',
    bg: '#fef2f2', bgDark: '#7f1d1d', border: '#ef4444',
    fg: '#991b1b', fgDark: '#fee2e2', accent: '#dc2626',
    badgeBg: '#fee2e2', badgeFg: '#b91c1c',
  },
  PRIMARY: {
    icon: FaCircle,
    label: 'Primary',
    bg: '#eff6ff', bgDark: '#1e40af', border: '#2563eb',
    fg: '#1e40af', fgDark: '#dbeafe', accent: '#1d4ed8',
    badgeBg: '#dbeafe', badgeFg: '#1e40af',
  },
  SECONDARY: {
    icon: FaRegCircle,
    label: 'Secondary',
    bg: '#f8fafc', bgDark: '#334155', border: '#94a3b8',
    fg: '#475569', fgDark: '#e2e8f0', accent: '#64748b',
    badgeBg: '#e2e8f0', badgeFg: '#475569',
  },
  LIGHT: {
    icon: FaSun,
    label: 'Light',
    bg: '#ffffff', bgDark: '#f1f5f9', border: '#e2e8f0',
    fg: '#334155', fgDark: '#475569', accent: '#94a3b8',
    badgeBg: '#f1f5f9', badgeFg: '#475569',
  },
  DARK: {
    icon: FaMoon,
    label: 'Dark',
    bg: '#1e293b', bgDark: '#0f172a', border: '#334155',
    fg: '#f1f5f9', fgDark: '#e2e8f0', accent: '#94a3b8',
    badgeBg: '#334155', badgeFg: '#e2e8f0',
  },
};

/** STYPE'a göre stil bilgisi döner. STYPE yoksa null. */
function getStypeStyle(stype) {
  if (!stype) return null;
  const key = String(stype).toUpperCase().trim();
  return STYPE_PRESETS[key] || null;
}

/** Bubble içinde STYPE rozeti — başlıkta sol üstte, yarı saydam zeminli */
const StypeBadge = ({ preset, dark = false }) => {
  if (!preset) return null;
  const Icon = preset.icon;
  return (
    <span
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 4,
        padding: '1px 6px', borderRadius: 999,
        fontSize: 9, fontWeight: 700, letterSpacing: '0.04em',
        textTransform: 'uppercase',
        background: dark ? 'rgba(255,255,255,0.16)' : preset.badgeBg,
        color: dark ? preset.fgDark : preset.badgeFg,
        flexShrink: 0,
      }}
    >
      <Icon size={9} />
      {preset.label}
    </span>
  );
};

// Yüklenme durumu için overlay
const LoadingOverlay = () => {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <BlinkBlur color="#32cd32" size="medium" text="JUBI" textColor="" />
    </div>
  );
};

// WhatsApp tarzı typing indicator - smooth giriş/çıkış + minimum görünme süresi
const PendingEventBubble = ({ count, isActive, minShowMs = 900, exitMs = 220 }) => {
  const [shouldRender, setShouldRender] = useState(false);
  const [visible, setVisible] = useState(false);
  const shownAtRef = useRef(0);
  const hideTimerRef = useRef(null);
  const unmountTimerRef = useRef(null);

  useEffect(() => {
    // Cleanup timers
    if (hideTimerRef.current) window.clearTimeout(hideTimerRef.current);
    if (unmountTimerRef.current) window.clearTimeout(unmountTimerRef.current);

    if (isActive && count > 0) {
      // mount + fade in
      setShouldRender(true);
      // next tick to allow transition
      window.requestAnimationFrame(() => setVisible(true));
      shownAtRef.current = Date.now();
      return;
    }

    if (!shouldRender) return;

    const elapsed = Date.now() - (shownAtRef.current || 0);
    const wait = Math.max(0, minShowMs - elapsed);
    hideTimerRef.current = window.setTimeout(() => {
      setVisible(false);
      unmountTimerRef.current = window.setTimeout(() => setShouldRender(false), exitMs);
    }, wait);
  }, [count, exitMs, isActive, minShowMs, shouldRender]);

  useEffect(() => {
    return () => {
      if (hideTimerRef.current) window.clearTimeout(hideTimerRef.current);
      if (unmountTimerRef.current) window.clearTimeout(unmountTimerRef.current);
    };
  }, []);

  if (!shouldRender) return null;

  return (
    <div
      className={`flex justify-center px-2 ${
        visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-2"
      }`}
      style={{ transition: "opacity 0.3s ease-out, transform 0.3s ease-out" }}
    >
      <div className="flex items-center gap-1.5 bg-white/95 rounded-full px-2.5 py-1 shadow border border-slate-200 text-[10.5px]">
        <div className="w-5 h-5 bg-gradient-to-br from-indigo-500 to-blue-500 rounded-full flex items-center justify-center text-white text-[10px] font-semibold">
          {count > 9 ? "9+" : count}
        </div>
        <div className="flex items-center gap-1">
          <div className="flex gap-0.75">
            <div
              className="w-1.5 h-1.5 bg-indigo-500 rounded-full animate-pulse"
            />
            <div
              className="w-1.5 h-1.5 bg-indigo-400 rounded-full animate-pulse"
              style={{ animationDelay: "0.15s" }}
            />
            <div
              className="w-1.5 h-1.5 bg-indigo-300 rounded-full animate-pulse"
              style={{ animationDelay: "0.3s" }}
            />
          </div>
          <span className="text-slate-600">
            {count > 1 ? `${count} yeni kayıt` : "Yeni kayıt"}
          </span>
        </div>
      </div>
    </div>
  );
};

const RunIndexSimulation = ({ runIndex = [] }) => {
  const [highlightedIndex, setHighlightedIndex] = useState(0);

  useEffect(() => {
    if (highlightedIndex < runIndex.length) {
      const timer = setTimeout(() => {
        setHighlightedIndex((prev) => prev + 1);
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [highlightedIndex, runIndex]);

  return (
    <div className="simulation-container">
      {runIndex.map((path, index) => (
        <div key={index} className="step-container">
          {/* Daire */}
          <div
            className={`circle ${index <= highlightedIndex ? "circle-active" : "circle-inactive"}`}
          >
            {index + 1}
          </div>

          {/* Yollar */}
          {index < runIndex.length - 1 && (
            <svg
              className="line"
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 100 2"
              preserveAspectRatio="none"
              fill="none"
            >
              <line
                x1="0"
                y1="1"
                x2="100"
                y2="1"
                stroke={index < highlightedIndex ? "rgb(59, 130, 246)" : "#e2e8f0"}
                strokeWidth="2"
                strokeDasharray={index < highlightedIndex ? "4" : "0"}
              />
            </svg>
          )}

          {/* Path İsmi */}
          <div className={`path-label ${index <= highlightedIndex ? "label-active" : "label-inactive"}`}>
            {path.split("/").slice(-1)[0]}
          </div>
        </div>
      ))}
    </div>
  );
};



const EventDetailModal = ({ isOpen, onClose, event, onDelete, runIndex, fetchEventFromServer }) => {
  const [activeTab, setActiveTab] = useState("Information");
  const [isDeleting, setIsDeleting] = useState(false);
  const eventDisabled = isEventDisabled(event);

  const handleDelete = async () => {
    setIsDeleting(true);
    try {
      const result = await onDelete(event.EVEID, event.TICID);
      setIsDeleting(false);
      if (result && result.success) {
        onClose();
      } else {
        alert("Event silme başarısız oldu.");
      }
    } catch (error) {
      setIsDeleting(false);
      alert("Event silme başarısız oldu.");
    }
  };

  if (!isOpen) return null;

  const fieldsToShow = [
    "EVEID", "TICID", "MEMID", "ETYPE", "FLOWID", "ESOURCEFILE",
    "FNAME", "TDESC", "FVALUE", "ALLVALUES", "LOEKZ", "VISIBLE",
  ];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white rounded-lg shadow-lg w-4/5 max-w-2xl">
        <div className="flex justify-between items-center px-6 py-4 bg-gray-100 border-b border-gray-300">
          <h2 className="text-lg font-bold text-gray-800">Event Details</h2>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-800 focus:outline-none">×</button>
        </div>

        <div className="flex border-b border-gray-300 text-sm">
          <button
            className={`flex-1 py-2 text-center ${activeTab === "Information" ? "border-b-2 border-blue-500 text-blue-500" : "text-gray-500 hover:text-blue-500"}`}
            onClick={() => setActiveTab("Information")}
          >
            Info
          </button>
          {!eventDisabled && (
            <button
              className={`flex-1 py-2 text-center ${activeTab === "Settings" ? "border-b-2 border-red-500 text-red-500" : "text-gray-500 hover:text-red-500"}`}
              onClick={() => setActiveTab("Settings")}
            >
              Sil
            </button>
          )}
          <button
            className={`flex-1 py-2 text-center ${activeTab === "Run Index" ? "border-b-2 border-blue-500 text-blue-500" : "text-gray-500 hover:text-blue-500"}`}
            onClick={() => setActiveTab("Run Index")}
          >
            Run Index
          </button>
        </div>

        <div className="p-6 text-sm">
          {activeTab === "Information" && (
            <div className="space-y-4 max-h-80 overflow-y-auto">
              {event && Object.entries(event)
                .filter(([key]) => fieldsToShow.includes(key))
                .map(([key, value]) => (
                  <div key={key} className="flex justify-between mt-1">
                    <span className="text-gray-600">{key}:</span>
                    <span className="text-black truncate">
                      {typeof value === "object" && value !== null
                        ? JSON.stringify(value, null, 2)
                        : (value !== undefined && value !== null ? String(value) : "")}
                    </span>
                  </div>
                ))}
            </div>
          )}

          {activeTab === "Settings" && !eventDisabled && (
            <div className="text-center space-y-6">
              <p className="text-gray-500">
                Bu event silinecektir. Silinen event listede gri/pasif olarak kalır.
              </p>
              <div className="flex justify-center space-x-4">
                <button
                  onClick={handleDelete}
                  className="bg-red-500 hover:bg-red-600 text-white px-4 py-2 rounded"
                  disabled={isDeleting}
                >
                  {isDeleting ? "Siliniyor..." : "Sil"}
                </button>
                <button
                  onClick={onClose}
                  className="bg-gray-300 hover:bg-gray-400 text-gray-800 px-4 py-2 rounded"
                >
                  İptal
                </button>
              </div>
            </div>
          )}

          {activeTab === "Run Index" && <RunIndexSimulation runIndex={runIndex} />}
        </div>
      </div>
    </div>
  );
};



const RightPanelBody = () => {
  // ÖNCE Context değerlerini al
  const { events, deleteEvent, SendEvent, loading, runIndex, selectedTicketId, eventCache, getEventCacheInfo, setEvents, removeEventFromCache, addEventToCache, deleteEventFromCache, currentInputField, markSendPerformanceStage, isSendingEvent, isLoadingEvents, isSwitchingTicket, setPendingCheckboxSelection } =
    useContext(TicketContext);
  const { user } = useContext(AuthContext);
  const { fetchEventFromServer, pendingEvents } = useContext(TicketWebSocketContext);
  
  // SONRA render sayacı - useRef ile
  const renderCount = useRef(0);
  renderCount.current++;
  
  // Events'i memoize et — tüm event'ler render edilir (disabled olanlar dahil).
  // LOEKZ/VISIBLE filtresi yok; UI'da isEventDisabled() ile gri/pasif gösterilir.
  const filteredEvents = useMemo(() => {
    return (events || []).filter((event, index, self) =>
      index === self.findIndex(e => String(e.EVEID) === String(event.EVEID))
    );
  }, [events]);
  
  // pendingEvents'i de memoize et
  const pendingEventCount = useMemo(() => pendingEvents.size, [pendingEvents]);
  const { toggleStar, isStarred } = useStarredEvents();

  // DEBUG: Event STYPE'larını ve "stype-benzeri" tüm field'ları console'a bas.
  // Backend'den hangi field adıyla geldiğini bulmak için derinlemesine arama yapar.
  useEffect(() => {
    if (!filteredEvents || filteredEvents.length === 0) return;
    const STYPE_VALUES = /^(SUCCESS|WARNING|DANGER|INFO|PRIMARY|SECONDARY|LIGHT|DARK)$/i;

    // eslint-disable-next-line no-console
    console.groupCollapsed(`[Events STYPE Debug] ${filteredEvents.length} event`);
    filteredEvents.forEach((ev) => {
      // 1) "stype" geçen tüm field'lar (case insensitive)
      const stypeKeys = Object.keys(ev).filter((k) => /stype/i.test(k));
      const stypeKeyValues = stypeKeys.reduce((acc, k) => { acc[k] = ev[k]; return acc; }, {});

      // 2) Hangi alanda "DANGER" / "SUCCESS" gibi bir bayrak değeri var?
      const valueLikeFields = Object.entries(ev)
        .filter(([, v]) => typeof v === 'string' && STYPE_VALUES.test(v.trim()))
        .reduce((acc, [k, v]) => { acc[k] = v; return acc; }, {});

      // eslint-disable-next-line no-console
      console.log(`EVEID=${ev.EVEID}  FNAME=${ev.FNAME || ''}`, {
        '> ev.STYPE (direct)':  ev.STYPE,
        '> stype* keys':        stypeKeyValues,
        '> tag-like values':    valueLikeFields,
        '> all keys':           Object.keys(ev).sort(),
        '> raw event':          ev,
      });
    });

    const counts = filteredEvents.reduce((acc, ev) => {
      const k = (ev.STYPE || '(none)').toString();
      acc[k] = (acc[k] || 0) + 1;
      return acc;
    }, {});
    // eslint-disable-next-line no-console
    console.log('STYPE counts (ev.STYPE direct read):', counts);
    // eslint-disable-next-line no-console
    console.groupEnd();
  }, [filteredEvents]);

  const [viewEvents, setViewEvents] = useState([]);
  const removalTimeoutsRef = useRef(new Map());
  const [recentlyAddedEventIds, setRecentlyAddedEventIds] = useState(new Set());
  const [selectedButtons, setSelectedButtons] = useState({});
  const prevRenderedEventIdsRef = useRef([]);

  useEffect(() => {
    // During a ticket switch, just update the ref so we don't animate already-present events
    if (isSwitchingTicket) {
      prevRenderedEventIdsRef.current = filteredEvents.map(event => String(event.EVEID));
      return;
    }

    const timeoutIds = [];
    const prevIds = new Set(prevRenderedEventIdsRef.current);
    const newIds = filteredEvents
      .map(event => String(event.EVEID))
      .filter(id => !prevIds.has(id));

    if (newIds.length > 0) {
      setRecentlyAddedEventIds(prev => {
        const updated = new Set(prev);
        newIds.forEach(id => {
          updated.add(id);
          const timeoutId = window.setTimeout(() => {
            setRecentlyAddedEventIds(current => {
              const next = new Set(current);
              next.delete(id);
              return next;
            });
          }, 1400);
          timeoutIds.push(timeoutId);
        });
        return updated;
      });

      if (selectedTicketId) {
        markSendPerformanceStage(selectedTicketId, 'rendered', { renderedIds: newIds });
      }
    }

    prevRenderedEventIdsRef.current = filteredEvents.map(event => String(event.EVEID));

    return () => {
      timeoutIds.forEach(clearTimeout);
    };
  }, [filteredEvents, markSendPerformanceStage, selectedTicketId, isSwitchingTicket]);
  
  // İlk 5 render'da detaylı log
  if (renderCount.current <= 5) {
  }
  
  // Her 50 render'da özet
  if (RightPanelBody.renderCount % 50 === 0) {
  }
  

  const bottomRef = useRef(null); // Scroll'u kontrol etmek için ref
  const scrollContainerRef = useRef(null);
  const stickToBottomRef = useRef(true);
  const scrollRafRef = useRef(null);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [addEventId, setAddEventId] = useState(''); // Add için event ID input
  const [openDropdownEventId, setOpenDropdownEventId] = useState(null);
  const [previewAttachment, setPreviewAttachment] = useState(null);
  const [spreadsheetData, setSpreadsheetData] = useState(null);
  const [spreadsheetLoading, setSpreadsheetLoading] = useState(false);

  useEffect(() => {
    if (!openDropdownEventId) return;
    const handleClickOutside = (e) => {
      const target = e.target;
      if (!target.closest(`[data-event-dropdown="${openDropdownEventId}"]`)) {
        setOpenDropdownEventId(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [openDropdownEventId]);

  useEffect(() => {
    if (!previewAttachment) {
      setSpreadsheetData(null);
      return;
    }
    const ctype = previewAttachment.CTYPE;
    if (!["csv", "xlsx", "xls"].includes(ctype)) return;

    let cancelled = false;
    setSpreadsheetLoading(true);
    setSpreadsheetData(null);

    (async () => {
      try {
        const res = await fetch(previewAttachment.URL);
        const buf = await res.arrayBuffer();
        const wb = XLSX.read(buf, { type: 'array' });
        if (cancelled) return;
        const ws = wb.Sheets[wb.SheetNames[0]];
        const rows = XLSX.utils.sheet_to_json(ws, { header: 1, defval: '' });
        if (rows.length === 0) {
          setSpreadsheetData({ headers: [], rows: [] });
        } else {
          setSpreadsheetData({ headers: rows[0], rows: rows.slice(1) });
        }
      } catch {
        if (!cancelled) setSpreadsheetData({ headers: [], rows: [], error: true });
      } finally {
        if (!cancelled) setSpreadsheetLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, [previewAttachment]);


  // Events değiştiğinde scroll yapma
  const [prevEventsLength, setPrevEventsLength] = useState(0);
  
  const scrollToBottom = useCallback((behavior = 'smooth') => {
    const container = scrollContainerRef.current;
    if (!container) return;

    if (typeof container.scrollTo === 'function') {
      container.scrollTo({ top: container.scrollHeight, behavior });
    } else {
      container.scrollTop = container.scrollHeight;
    }
  }, []);

  const [isNearBottom, setIsNearBottom] = useState(true);

  const updateIsNearBottom = useCallback(() => {
    const container = scrollContainerRef.current;
    if (!container) return;
    const distanceFromBottom =
      container.scrollHeight - container.scrollTop - container.clientHeight;
    const near = distanceFromBottom < 120;
    stickToBottomRef.current = near;
    setIsNearBottom(near);
  }, []);

  useEffect(() => {
    updateIsNearBottom();
  }, [updateIsNearBottom]);

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;
    let ticking = false;
    const onScroll = () => {
      if (!ticking) {
        ticking = true;
        requestAnimationFrame(() => {
          updateIsNearBottom();
          ticking = false;
        });
      }
    };
    container.addEventListener('scroll', onScroll, { passive: true });
    return () => container.removeEventListener('scroll', onScroll);
  }, [updateIsNearBottom]);

  // İSTENEN DAVRANIŞ:
  // Event render / remove dahil, events değiştiği her durumda otomatik en alta kaydır.
  useEffect(() => {
    if (!events) return;

    // Kullanıcı yukarı scroll’da olsa bile zorla alta kilitle
    stickToBottomRef.current = true;
    setIsNearBottom(true);

    const behavior = prevEventsLength === 0 ? 'auto' : 'smooth';
    if (scrollRafRef.current) {
      cancelAnimationFrame(scrollRafRef.current);
    }
    scrollRafRef.current = requestAnimationFrame(() => {
      scrollToBottom(behavior);
    });

    setPrevEventsLength(Array.isArray(events) ? events.length : 0);

    return () => {
      if (scrollRafRef.current) {
        cancelAnimationFrame(scrollRafRef.current);
      }
    };
  }, [events, prevEventsLength, scrollToBottom]);

  // viewEvents remove animasyonu gibi UI değişimlerinde de alta kaydır (events değişmese bile)
  useEffect(() => {
    if (!Array.isArray(viewEvents)) return;
    stickToBottomRef.current = true;
    setIsNearBottom(true);
    if (scrollRafRef.current) cancelAnimationFrame(scrollRafRef.current);
    scrollRafRef.current = requestAnimationFrame(() => {
      scrollToBottom('smooth');
    });
    return () => {
      if (scrollRafRef.current) cancelAnimationFrame(scrollRafRef.current);
    };
  }, [viewEvents.length, scrollToBottom]);

  // PERFORMANS: formatBubbleTime her render'da tüm event'ler için tekrar Date parse
  // ediyordu. Cache ile event listesi yeniden render olduğunda aynı Date string'i
  // için sonuç yeniden hesaplanmaz.
  const bubbleTimeCacheRef = useRef(new Map());
  const formatBubbleTime = useCallback((andat) => {
    if (!andat) return '';
    const cache = bubbleTimeCacheRef.current;
    const cached = cache.get(andat);
    if (cached !== undefined) return cached;
    let result;
    try {
      const d = new Date(andat);
      result = Number.isNaN(d.getTime())
        ? String(andat)
        : d.toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' });
    } catch {
      result = String(andat);
    }
    // Cache boyutunu sınırla (LRU yerine basit clear)
    if (cache.size > 500) cache.clear();
    cache.set(andat, result);
    return result;
  }, []);

  // PERFORMANS: safeParseJson her render'da her event için aynı büyük JSON'u parse
  // ediyordu. Event EVEID'sine göre cache. Event silindiğinde cache'ten de temizlensin
  // diye küçük LRU mantığı.
  const jsonCacheRef = useRef(new Map());
  const parseJsonCached = useCallback((cacheKey, raw, maxLen = 200000) => {
    if (!cacheKey) return safeParseJson(raw, maxLen);
    const cache = jsonCacheRef.current;
    const cached = cache.get(cacheKey);
    if (cached && cached.raw === raw) return cached.parsed;
    const parsed = safeParseJson(raw, maxLen);
    if (cache.size > 300) cache.clear();
    cache.set(cacheKey, { raw, parsed });
    return parsed;
  }, []);

  // Ticket değişince cache'i sıfırla — eski ticket'ın event'lerini tutmasın
  useEffect(() => {
    jsonCacheRef.current.clear();
    bubbleTimeCacheRef.current.clear();
  }, [selectedTicketId]);

  const getAttachIcon = (ctype) => {
    switch (ctype) {
      case "png": case "jpg": case "jpeg": case "gif": case "webp": case "svg":
        return { Icon: FaFileImage, color: "text-emerald-500" };
      case "pdf":
        return { Icon: FaFilePdf, color: "text-red-500" };
      case "mp4": case "avi": case "mov": case "webm":
        return { Icon: FaFileVideo, color: "text-blue-500" };
      case "csv": case "xlsx": case "xls":
        return { Icon: FaFileExcel, color: "text-green-600" };
      default:
        return { Icon: FaFileAlt, color: "text-slate-400" };
    }
  };

  const isImageType = (ctype) => ["png", "jpg", "jpeg", "gif", "webp", "svg"].includes(ctype);
  const isVideoType = (ctype) => ["mp4", "avi", "mov", "webm"].includes(ctype);
  const isSpreadsheetType = (ctype) => ["csv", "xlsx", "xls"].includes(ctype);

  const renderAttachment = (attach) => {
    if (!attach) return null;
    const { Icon, color } = getAttachIcon(attach.CTYPE);
    const isImage = isImageType(attach.CTYPE);

    return (
      <button
        onClick={() => setPreviewAttachment(attach)}
        className="group/att flex items-center gap-1.5 rounded-md border border-slate-100 bg-slate-50/60 px-2 py-1 text-left transition-colors hover:bg-slate-100/80 hover:border-slate-200 cursor-pointer"
      >
        {isImage ? (
          <img
            src={attach.URL}
            alt={attach.basename}
            className="w-6 h-6 object-cover rounded flex-shrink-0"
          />
        ) : (
          <Icon size={13} className={`${color} flex-shrink-0`} />
        )}
        <span className="text-[9px] text-slate-500 truncate max-w-[100px] group-hover/att:text-slate-700">
          {attach.basename}
        </span>
      </button>
    );
  };

  const openDetailModal = (event) => {
    setSelectedEvent(event);
    setIsModalOpen(true);
  };

  const closeDetailModal = () => {
    setSelectedEvent(null);
    setIsModalOpen(false);
  };
  



  const renderEventDetails = (FVALUE, FNAME, ALLVALUES, eventId) => {
    // FVALUE'nun string olduğundan emin ol
    if (typeof FVALUE !== 'string') {
      console.warn('FVALUE is not a string:', FVALUE);
      return {
        isWorkflow: false,
        content: (
          <div>
            <span>Invalid data format</span>
          </div>
        )
      };
    }

    // YOUTUBE tipi: FVALUE içindeki youtube linkini video preview olarak göster
    if (String(FNAME || '').toUpperCase() === 'YOUTUBE') {
      const extractYouTubeId = (raw) => {
        if (!raw) return null;
        const text = String(raw).trim();
        if (!text) return null;

        // Doğrudan 11 karakterlik ID gelmiş olabilir
        if (/^[a-zA-Z0-9_-]{11}$/.test(text)) return text;

        try {
          const url = new URL(text);
          const host = url.hostname.replace(/^www\./, '').toLowerCase();

          // youtu.be/<id>
          if (host === 'youtu.be') {
            const id = url.pathname.split('/').filter(Boolean)[0];
            return id && /^[a-zA-Z0-9_-]{11}$/.test(id) ? id : null;
          }

          // youtube.com/watch?v=<id>
          if (host === 'youtube.com' || host === 'm.youtube.com' || host === 'music.youtube.com') {
            const v = url.searchParams.get('v');
            if (v && /^[a-zA-Z0-9_-]{11}$/.test(v)) return v;

            // youtube.com/shorts/<id> | /embed/<id>
            const parts = url.pathname.split('/').filter(Boolean);
            const idx = parts.findIndex((p) => p === 'shorts' || p === 'embed');
            if (idx >= 0 && parts[idx + 1] && /^[a-zA-Z0-9_-]{11}$/.test(parts[idx + 1])) {
              return parts[idx + 1];
            }
          }
        } catch {
          // URL parse edilemezse, metin içinde bir ID yakalamayı dene
        }

        const m = text.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/shorts\/|youtube\.com\/embed\/)([a-zA-Z0-9_-]{11})/);
        return m?.[1] || null;
      };

      const videoId = extractYouTubeId(FVALUE);
      const href = typeof FVALUE === 'string' ? FVALUE : '';

      if (!videoId) {
        return {
          isWorkflow: false,
          content: (
            <div className="space-y-2">
              <div className="text-[10.5px] text-slate-500">YouTube linki algılanamadı</div>
              <a
                href={href}
                target="_blank"
                rel="noreferrer"
                className="text-blue-600 hover:text-blue-700 underline break-all"
              >
                {href || 'Link'}
              </a>
            </div>
          )
        };
      }

      const embedUrl = `https://www.youtube-nocookie.com/embed/${videoId}?rel=0&modestbranding=1`;
      const watchUrl = `https://www.youtube.com/watch?v=${videoId}`;

      return {
        isWorkflow: false,
        content: (
          <div className="w-full max-w-xl">
            <div className="rounded-xl border border-slate-200 bg-white/95 shadow-sm overflow-hidden">
              <div className="aspect-video w-full bg-slate-100">
                <iframe
                  title={`youtube-${videoId}`}
                  src={embedUrl}
                  className="h-full w-full"
                  loading="lazy"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  allowFullScreen
                  referrerPolicy="strict-origin-when-cross-origin"
                  sandbox="allow-scripts allow-same-origin allow-presentation allow-popups"
                />
              </div>
              <div className="flex items-center justify-between gap-3 px-3 py-2">
                <div className="min-w-0 text-[10.5px] text-slate-600 truncate">
                  YouTube Video
                </div>
                <a
                  href={watchUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="text-[10.5px] font-semibold text-blue-600 hover:text-blue-700 whitespace-nowrap"
                  title="YouTube'da aç"
                >
                  Aç →
                </a>
              </div>
            </div>
          </div>
        )
      };
    }

    try {
      const parsed = parseJsonCached(`fv-${eventId}`, FVALUE, 200000);
      if (!parsed.ok) {
        throw new Error('invalid-json');
      }
      const parsedFVALUE = parsed.value;
      
      // TREESTRUCTURE kontrolü
      if (FNAME === 'TREESTRUCTURE') {
        
        return {
          isWorkflow: false,
          content: (
            <div className="w-full">
              <TreeStructure data={parsedFVALUE} SendEvent={SendEvent} />
            </div>
          )
        };
      }

      // Workflow kontrolü
      if (parsedFVALUE.workflow && parsedFVALUE.workflow.id) {
        
       
        
        return {
          isWorkflow: true,
          content: (
            <div className="w-full">
              <FlowChart 
                workflowData={parsedFVALUE} 
                uniqueId={`event-${eventId}`}
              />
            </div>
          )
        };
      }

      // Eğer tablo yapısı varsa tablo renderla
      if (parsedFVALUE.table_content && parsedFVALUE.table_header) {
        // table_row_selectable parametresi — default true
        const showSelectButton = parsedFVALUE.table_row_selectable !== false;
        // table_row_selectType: 'button' (default) | 'checkbox'
        const selectType = parsedFVALUE.table_row_selectType === 'checkbox' ? 'checkbox' : 'button';
        const eventFNAMEValue = parsedFVALUE.FNAME || FNAME;
        const columnKey = parsedFVALUE.table_column_key;
        return {
          isWorkflow: false,
          isEventTable: true,
          content: (
            <EventTable
              tableColumnKey={columnKey}
              tableContent={parsedFVALUE.table_content}
              tableHeader={parsedFVALUE.table_header}
              showSelectButton={showSelectButton}
              selectType={selectType}
              eventFNAME={eventFNAMEValue}
              onCheckboxSelectionChange={(values, rawRows) => {
                setPendingCheckboxSelection(
                  values.length > 0
                    ? { FNAME: eventFNAMEValue, values, rawRows, columnKey, eventId: String(eventId) }
                    : null
                );
              }}
            />
          )
        };
      }

      // Eğer dizi ise ve içinde DESC + VALUE varsa butonları renderla
      if (
        Array.isArray(parsedFVALUE) &&
        parsedFVALUE.some((item) => item.DESC && item.VALUE)
      ) {
        const buttonGroupKey = `fvalue-${eventId}`;
        return {
          isWorkflow: false,
          content: (
            <div className="flex flex-wrap gap-1">
              {parsedFVALUE.map((item, index) => {
                const eventData = {
                  inputName: FNAME,
                  message: item.VALUE,
                  data:
                    item.data && typeof item.data === "object"
                      ? JSON.stringify(item.data)
                      : (item.data && typeof item.data === 'string' ? item.data : null),
                };
                const isSelected = selectedButtons[buttonGroupKey] === index;

                return (
                  <button
                    key={index}
                    onClick={() => {
                      setSelectedButtons(prev => ({ ...prev, [buttonGroupKey]: index }));
                      SendEvent(eventData, [], 'RightPanelBody-FVALUEButton');
                    }}
                    className={`px-2 py-1 text-[10px] font-medium rounded-md transition-colors ${
                      isSelected
                        ? 'bg-slate-700 text-white'
                        : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                    }`}
                  >
                    {item.DESC}
                  </button>
                );
              })}
            </div>
          )
        };
      }
    } catch (error) {
    }

    const isHtml = typeof FVALUE === 'string' && /<\/?[a-z][\s\S]*>/i.test(FVALUE);
    if (isHtml) {
      const safeHtml = sanitizeHtml(FVALUE);
      return {
        isWorkflow: false,
        content: (
          <div
            className="overflow-x-auto max-w-full text-[11px] leading-snug"
            dangerouslySetInnerHTML={{ __html: safeHtml }}
          />
        )
      };
    }

    return {
      isWorkflow: false,
      content: (
        <span className="text-[11px] leading-snug font-normal tracking-tight">{typeof FVALUE === 'string' ? FVALUE : 'Invalid data'}</span>
      )
    };
  };

  // TDESC detaylarını render eden fonksiyon - basitleştirildi
  const renderTDESCDetails = (TDESC, eventId) => {
    // TDESC'nun string olduğundan emin ol
    if (typeof TDESC !== 'string') {
      console.warn('TDESC is not a string:', TDESC);
      return <p>Invalid data format</p>;
    }

    try {
      const parsed = parseJsonCached(`td-${eventId}`, TDESC, 200000);
      if (!parsed.ok) {
        throw new Error('invalid-json');
      }
      const parsedTDESC = parsed.value;
      if (parsedTDESC.table_content && parsedTDESC.table_header) {
        const showSelectButton = parsedTDESC.table_row_selectable !== false;
        const selectType = parsedTDESC.table_row_selectType === 'checkbox' ? 'checkbox' : 'button';
        const eventFNAMEValue = parsedTDESC.FNAME;
        const columnKey = parsedTDESC.table_column_key;
        return (
          <EventTable
            tableColumnKey={columnKey}
            tableContent={parsedTDESC.table_content}
            tableHeader={parsedTDESC.table_header}
            showSelectButton={showSelectButton}
            selectType={selectType}
            eventFNAME={eventFNAMEValue}
            onCheckboxSelectionChange={(values, rawRows) => {
              setPendingCheckboxSelection(
                values.length > 0
                  ? { FNAME: eventFNAMEValue, values, rawRows, columnKey, eventId: String(eventId) }
                  : null
              );
            }}
          />
        );
      }
      
      if (Array.isArray(parsedTDESC) && parsedTDESC.some(item => item.DESC && item.VALUE)) {
        const buttonGroupKey = `tdesc-${eventId}`;
        return (
          <div className="flex flex-wrap gap-1">
            {parsedTDESC.map((item, index) => {
              const eventData = {
                inputName: "TDESC", 
                message: item.VALUE,
                data: item.data ? (typeof item.data === 'object' ? JSON.stringify(item.data) : (typeof item.data === 'string' ? item.data : null)) : null
              };
              const isSelected = selectedButtons[buttonGroupKey] === index;
              
              return (
                <button
                  key={index}
                  onClick={() => {
                    setSelectedButtons(prev => ({ ...prev, [buttonGroupKey]: index }));
                    SendEvent(eventData, [], 'RightPanelBody-TDESCButton');
                  }}
                  className={`px-2 py-1 text-[10px] font-medium rounded-md transition-colors ${
                    isSelected
                      ? 'bg-slate-700 text-white'
                      : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                  }`}
                >
                  {item.DESC}
                </button>
              );
            })}
          </div>
        );
      }
    } catch (error) {
    }

    const isHtml = typeof TDESC === 'string' && /<\/?[a-z][\s\S]*>/i.test(TDESC);
    if (isHtml) {
      const safeHtml = sanitizeHtml(TDESC);
      return (
        <div
          className="overflow-x-auto max-w-full"
          dangerouslySetInnerHTML={{ __html: safeHtml }}
        />
      );
    }

    return <p>{typeof TDESC === 'string' ? TDESC : 'Invalid data'}</p>;
  };

  useEffect(() => {
    const currentIds = new Set((filteredEvents || []).map((event) => String(event.EVEID)));
    setViewEvents((prev) => {
      const next = (filteredEvents || []).map((event) => ({
        ...event,
        _removing: false,
      }));

      if (!isSwitchingTicket) {
        prev.forEach((event) => {
          const id = String(event.EVEID);
          if (!currentIds.has(id)) {
            next.push({ ...event, _removing: true });
          }
        });
      }

      return next;
    });
  }, [filteredEvents, isSwitchingTicket]);

  useEffect(() => {
    viewEvents.forEach((event) => {
      const id = String(event.EVEID);
      if (!event._removing) {
        if (removalTimeoutsRef.current.has(id)) {
          window.clearTimeout(removalTimeoutsRef.current.get(id));
          removalTimeoutsRef.current.delete(id);
        }
        return;
      }
      if (removalTimeoutsRef.current.has(id)) return;

      const timeoutId = window.setTimeout(() => {
        setViewEvents((prev) => prev.filter((item) => String(item.EVEID) !== id));
        removalTimeoutsRef.current.delete(id);
      }, 180);
      removalTimeoutsRef.current.set(id, timeoutId);
    });
  }, [viewEvents]);

  useEffect(() => {
    return () => {
      removalTimeoutsRef.current.forEach((timeoutId) => window.clearTimeout(timeoutId));
      removalTimeoutsRef.current.clear();
    };
  }, []);

  // Ticket geçişi bittiğinde gecikmiş giriş animasyonlarını temizle
  useEffect(() => {
    if (!isSwitchingTicket) {
      setRecentlyAddedEventIds(prev => (prev.size > 0 ? new Set() : prev));
    }
  }, [isSwitchingTicket]);

  const hasEvents = viewEvents.length > 0 || filteredEvents.length > 0;

  return (
    <div
      className={`relative flex h-full w-full overflow-hidden ${
        hasEvents ? "bg-slate-100" : "bg-slate-900"
      } ${isSendingEvent ? 'pointer-events-none' : ''}`}
    >
      {hasEvents && (
        <>
          <div
            aria-hidden="true"
            className="pointer-events-none absolute inset-0"
            style={{
              backgroundImage: `url(${RPBodyBG})`,
              backgroundSize: "cover",
              backgroundPosition: "center",
              backgroundRepeat: "no-repeat",
              opacity: 0.06,
            }}
          />
          <div
            aria-hidden="true"
            className="pointer-events-none absolute inset-0"
            style={{
              background: "linear-gradient(135deg, rgba(248,250,252,0.7) 0%, rgba(226,232,240,0.6) 45%, rgba(203,213,225,0.5) 100%)",
            }}
          />
        </>
      )}

      <div className="relative flex h-full w-full flex-col">
        <div className="pointer-events-none absolute right-3 bottom-3 sm:right-4 sm:bottom-4 z-20">
          <div className="pointer-events-auto">
            <PendingEventBubble count={pendingEventCount} isActive={pendingEventCount > 0} />
          </div>
        </div>
        <div className="flex-1 overflow-hidden px-1 sm:px-3">
          <div
            ref={scrollContainerRef}
            className="flex h-full flex-col gap-1 overflow-y-auto pr-1 sm:pr-2"
            style={{
              scrollbarWidth: "thin",
              pointerEvents: isSwitchingTicket ? 'none' : undefined,
              transform: 'translateZ(0)',
              contain: 'layout style',
            }}
          >
            {hasEvents ? (
              <>
                {viewEvents.map((event) => {
                  const eventId = String(event.EVEID);

                  if (
                    event.FNAME === "WebSocket event" ||
                    event.FVALUE === "WebSocket'ten gelen event" ||
                    event.member?.uname === "WebSocket User"
                  ) {
                    return null;
                  }

                  const isNewEvent = recentlyAddedEventIds.has(eventId);
                  const bubbleAnimationClass = (isNewEvent && !isSwitchingTicket) ? "chat-bubble-enter" : "";
                  const removalClass = event._removing ? "opacity-0 translate-y-1" : "opacity-100";
                  const eventDetailsResult =
                    event.FVALUE_SWITCH === 1
                      ? renderEventDetails(event.FVALUE, event.FNAME, event.ALLVALUES, event.EVEID)
                      : { isWorkflow: false, content: null };
                  const isWorkflowCard =
                    eventDetailsResult?.isWorkflow || event.FNAME === "TREESTRUCTURE";

                  const hasEventTable = eventDetailsResult?.isEventTable === true;
                  const disabled = isEventDisabled(event);

                  if (event.FNAME === "CHANGEFLOWID") {
                    return (
                      <div
                        key={eventId}
                        className={`flex w-full justify-center ${removalClass}`}
                        style={{
                          transition: 'opacity 0.2s ease-out, transform 0.2s ease-out',
                          ...(disabled ? { opacity: 0.6, pointerEvents: 'none', filter: 'grayscale(0.5)' } : {}),
                        }}
                      >
                        <div className="inline-flex items-center gap-1.5 rounded-full border border-slate-100 bg-white/90 px-2.5 py-1 text-[9.5px] font-medium text-slate-500">
                          <span className="inline-flex h-1.5 w-1.5 rounded-full bg-emerald-400" />
                          <span className="tracking-wide">{event.FVALUE}</span>
                        </div>
                      </div>
                    );
                  }

                  if (isWorkflowCard) {
                    const cardPreset = getStypeStyle(event.STYPE);
                    const cardStyle = cardPreset ? {
                      background: cardPreset.bg,
                      borderColor: `${cardPreset.border}66`,
                      borderLeft: `4px solid ${cardPreset.border}`,
                      boxShadow: `0 1px 3px ${cardPreset.border}1f`,
                    } : undefined;
                    return (
                      <div
                        key={eventId}
                        className={`flex w-full justify-start ${bubbleAnimationClass} ${removalClass}`}
                        style={{
                          transition: 'opacity 0.2s ease-out, transform 0.2s ease-out',
                          ...(disabled ? { opacity: 0.6, filter: 'grayscale(0.5)' } : {}),
                        }}
                      >
                        <article
                          className={`rounded-lg border ${
                            hasEventTable ? 'w-[90%] max-w-[90%]' : 'w-full max-w-3xl'
                          } ${
                            cardPreset
                              ? ''
                              : disabled
                              ? 'border-slate-200 bg-slate-50 cursor-default'
                              : 'border-slate-100 bg-white shadow-[0_1px_3px_rgba(0,0,0,0.04)]'
                          }`}
                          style={{
                            ...(cardStyle || {}),
                            ...(disabled ? { pointerEvents: 'none' } : {}),
                          }}
                        >
                          <header
                            className={`flex items-center border-b px-3 py-1.5 ${
                              cardPreset ? '' : 'border-slate-50 bg-slate-50/60'
                            }`}
                            style={cardPreset ? {
                              borderColor: `${cardPreset.border}33`,
                              background: `${cardPreset.border}0d`,
                            } : undefined}
                          >
                            {cardPreset && (
                              <StypeBadge preset={cardPreset} />
                            )}
                            {!disabled && (
                              <div className="ml-auto flex items-center gap-2">
                                <button
                                  onClick={() => toggleStar(event)}
                                  className={`transition-colors ${isStarred(event.EVEID) ? 'text-amber-400 hover:text-amber-300' : 'text-slate-300 hover:text-amber-400'}`}
                                  title={isStarred(event.EVEID) ? 'Yıldızı kaldır' : 'Yıldızla'}
                                >
                                  {isStarred(event.EVEID) ? <FaStar size={13} /> : <FaRegStar size={13} />}
                                </button>
                                <button
                                  onClick={() => openDetailModal(event)}
                                  className="text-slate-400 transition-colors hover:text-slate-600"
                                  title="Event detayları"
                                >
                                  <FaEllipsisV />
                                </button>
                              </div>
                            )}
                          </header>

                          {(event.TDESC_SWITCH == 1 || (hasEventTable && event.TDESC)) && (
                            <div className="border-b border-slate-50 bg-slate-50/40 px-3 py-1.5 text-[10.5px] font-semibold text-slate-600">
                              {renderTDESCDetails(event.TDESC, event.EVEID)}
                            </div>
                          )}

                          <div className="event-content-wrapper px-3 py-2">
                            <div className="event-content text-[10.5px] leading-normal">
                              {eventDetailsResult?.content}
                            </div>
                          </div>

                          {(event.ESOURCEFILE_SWITCH == 1 ||
                            (event.ATTACHES && event.ATTACHES.length > 0)) && (
                            <div className="space-y-1.5 border-t border-slate-50 bg-slate-50/40 px-3 py-2">
                              {event.ESOURCEFILE_SWITCH == 1 && (
                                <div className="text-[10px] uppercase tracking-wide text-slate-400">
                                  {event.ESOURCEFILE}
                                </div>
                              )}
                              {event.ATTACHES?.length > 0 && (
                                <div className="flex flex-wrap gap-2">
                                  {event.ATTACHES.map((attach) => (
                                    <div key={attach.ATTID}>{renderAttachment(attach)}</div>
                                  ))}
                                </div>
                              )}
                            </div>
                          )}

                          <footer className="flex items-center justify-end gap-1.5 border-t border-slate-50 bg-slate-50/40 px-3 py-1.5 text-[9px] text-slate-400">
                            <span>{formatBubbleTime(event.ANDAT)}</span>
                            {event.VALIDATE === 1 ? (
                              <FaCheckCircle className="text-emerald-400" size={12} />
                            ) : (
                              <FaExclamationCircle className="text-red-400" size={12} />
                            )}
                          </footer>
                        </article>
                      </div>
                    );
                  }

                  const isLeftAligned = event.position === "L";

                  // STYPE'a göre tema (varsa). useMemo gerekmez — basit lookup.
                  const stypePreset = getStypeStyle(event.STYPE);
                  const usesDarkVariant = stypePreset && (stypePreset.label === 'Dark');
                  // Normal akışta sağ taraf koyu, sol taraf açıktır.
                  // STYPE varsa hem sağ hem sol için STYPE renklerini uygula.
                  const stypeStyle = stypePreset
                    ? (isLeftAligned || !usesDarkVariant
                        ? {
                            background: stypePreset.bg,
                            color: stypePreset.fg,
                            borderLeft: `3px solid ${stypePreset.border}`,
                            border: `1px solid ${stypePreset.border}33`,
                            borderLeftWidth: '3px',
                            boxShadow: `0 1px 2px ${stypePreset.border}1f`,
                          }
                        : {
                            background: stypePreset.bgDark,
                            color: stypePreset.fgDark,
                            borderLeft: `3px solid ${stypePreset.border}`,
                            boxShadow: `0 1px 2px rgba(0,0,0,0.15)`,
                          })
                    : null;

                  return (
                    <div
                      key={eventId}
                      className={`flex w-full ${removalClass} ${
                        isLeftAligned ? "justify-start" : "justify-end"
                      }`}
                      style={{
                        transition: 'opacity 0.2s ease-out, transform 0.2s ease-out',
                        ...(disabled ? { opacity: 0.6, filter: 'grayscale(0.5)' } : {}),
                      }}
                    >
                      <article
                        data-event-dropdown={eventId}
                        className={`group relative flex min-w-0 flex-col rounded-xl px-2.5 py-1.5 text-[11px] ${
                          hasEventTable
                            ? "w-[90%] max-w-[90%]"
                            : "max-w-[85%] sm:max-w-[75%]"
                        } ${
                          // STYPE varsa Tailwind tema sınıflarını ATLA — inline style kullanılacak
                          stypePreset
                            ? `${bubbleAnimationClass}`
                            : disabled
                            ? `border border-slate-200 bg-slate-100 text-slate-400 cursor-default ${bubbleAnimationClass}`
                            : isLeftAligned
                            ? `border border-slate-100 bg-white text-slate-700 shadow-[0_1px_2px_rgba(0,0,0,0.04)] ${bubbleAnimationClass}`
                            : `bg-slate-600 text-white/95 shadow-[0_1px_2px_rgba(0,0,0,0.08)] ${bubbleAnimationClass}`
                        }`}
                        style={{
                          ...(stypeStyle || {}),
                          ...(disabled ? { pointerEvents: 'none' } : {}),
                        }}
                      >
                        {!disabled && (
                          <button
                            data-dropdown-trigger={eventId}
                            onClick={(e) => {
                              e.stopPropagation();
                              setOpenDropdownEventId((prev) => (prev === eventId ? null : eventId));
                            }}
                            className={`absolute top-1 right-2 p-0.5 rounded opacity-0 group-hover:opacity-100 transition-opacity ${
                              isLeftAligned || stypePreset ? "text-slate-400 hover:text-slate-600" : "text-white/50 hover:text-white/80"
                            }`}
                            title="Menü"
                          >
                            <FaChevronDown size={10} />
                          </button>
                        )}
                        {/* STYPE rozeti — sol üstte, içerikten önce */}
                        {stypePreset && (
                          <div style={{ marginBottom: 4 }}>
                            <StypeBadge preset={stypePreset} dark={usesDarkVariant && !isLeftAligned} />
                          </div>
                        )}
                        {(() => {
                          const shouldShowTdesc = event.TDESC_SWITCH == 1 || (hasEventTable && event.TDESC);
                          return shouldShowTdesc;
                        })() && (
                          <div className={`rounded px-1.5 py-0.5 mb-1 text-[9.5px] font-semibold ${
                            stypePreset
                              ? ''
                              : isLeftAligned
                              ? "bg-slate-50 text-slate-600"
                              : "bg-white/10 text-white/80"
                          }`}
                          style={stypePreset ? {
                            background: usesDarkVariant && !isLeftAligned ? 'rgba(255,255,255,0.08)' : `${stypePreset.border}14`,
                            color: usesDarkVariant && !isLeftAligned ? stypePreset.fgDark : stypePreset.fg,
                          } : undefined}>
                            {renderTDESCDetails(event.TDESC, event.EVEID)}
                          </div>
                        )}
                        <div className="flex flex-nowrap items-end gap-x-3 w-full">
                          <div className="min-w-0 flex-1 event-content-wrapper event-content">
                            {event.FVALUE_SWITCH == 1 && (eventDetailsResult?.content ?? "No content")}
                            {event.ESOURCEFILE_SWITCH == 1 && (
                              <span className={`text-[8px] uppercase tracking-wider block mt-0.5 ${
                                stypePreset
                                  ? ''
                                  : isLeftAligned ? "text-slate-400" : "text-white/40"
                              }`}
                              style={stypePreset ? { color: stypePreset.accent, opacity: 0.85 } : undefined}
                              >
                                {event.ESOURCEFILE}
                              </span>
                            )}
                            {event.ATTACHES?.length > 0 && (
                              <div className="flex flex-wrap gap-1 mt-1">
                                {event.ATTACHES.map((attach) => (
                                  <div key={attach.ATTID}>{renderAttachment(attach)}</div>
                                ))}
                              </div>
                            )}
                          </div>
                          <div
                            className={`flex items-center gap-1 flex-shrink-0 ml-auto text-[9px] ${
                              stypePreset
                                ? ''
                                : isLeftAligned ? "text-slate-300" : "text-white/50"
                            }`}
                            style={{
                              marginTop: 1,
                              ...(stypePreset ? { color: usesDarkVariant && !isLeftAligned ? stypePreset.fgDark : stypePreset.accent, opacity: 0.75 } : {}),
                            }}
                          >
                            <span className="tabular-nums">{formatBubbleTime(event.ANDAT)}</span>
                            {event.VALIDATE === 1 ? (
                              <FaCheckCircle className="text-emerald-400/80" size={9} />
                            ) : (
                              <FaExclamationCircle className="text-red-400/80" size={9} />
                            )}
                          </div>
                        </div>
                        {openDropdownEventId === eventId && (() => {
                          const trigger = document.querySelector(`[data-dropdown-trigger="${eventId}"]`);
                          if (!trigger) return null;
                          const rect = trigger.getBoundingClientRect();
                          return createPortal(
                            <div
                              style={{
                                position: 'fixed',
                                top: rect.bottom + 4,
                                left: rect.right - 90,
                                zIndex: 999999,
                              }}
                              data-event-dropdown={eventId}
                            >
                              <div className="min-w-[90px] rounded-md border border-slate-100 bg-white py-0.5 shadow-lg">
                                <button
                                  onClick={() => {
                                    toggleStar(event);
                                    setOpenDropdownEventId(null);
                                  }}
                                  className="flex w-full items-center gap-1.5 px-2.5 py-1 text-left text-[10px] hover:bg-slate-50 text-slate-600"
                                >
                                  {isStarred(event.EVEID) ? <FaStar className="text-amber-400" size={10} /> : <FaRegStar size={10} />}
                                  <span>{isStarred(event.EVEID) ? "Yıldızı kaldır" : "Yıldızla"}</span>
                                </button>
                                <button
                                  onClick={() => {
                                    openDetailModal(event);
                                    setOpenDropdownEventId(null);
                                  }}
                                  className="flex w-full items-center gap-1.5 px-2.5 py-1 text-left text-[10px] hover:bg-slate-50 text-slate-600"
                                >
                                  <FaCog size={10} className="text-slate-400" />
                                  <span>Settings</span>
                                </button>
                              </div>
                            </div>,
                            document.body
                          );
                        })()}
                      </article>
                    </div>
                  );
                })}
              </>
            ) : (
              <div className="flex h-full w-full flex-1 flex-col items-center justify-center bg-slate-900/92 text-center text-white">
                <h1 className="text-5xl font-semibold uppercase tracking-[0.35em] text-white/80">
                  JUBI
                </h1>
                <p className="mt-4 text-sm text-white/60">Henüz görüntülenecek event yok.</p>
              </div>
            )}
            <div ref={bottomRef} />
          </div>
        </div>

        <EventDetailModal
          isOpen={isModalOpen}
          onClose={closeDetailModal}
          event={selectedEvent}
          runIndex={runIndex}
          onDelete={deleteEvent}
          fetchEventFromServer={fetchEventFromServer}
        />
      </div>

      {previewAttachment && (() => {
        const att = previewAttachment;
        const isImage = isImageType(att.CTYPE);
        const isVideo = isVideoType(att.CTYPE);
        const isPdf = att.CTYPE === "pdf";
        const isSpreadsheet = isSpreadsheetType(att.CTYPE);
        const { Icon, color } = getAttachIcon(att.CTYPE);

        return (
          <div
            className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/70"
            onClick={() => setPreviewAttachment(null)}
          >
            <div
              className="relative flex flex-col bg-white rounded-xl shadow-2xl overflow-hidden"
              style={{ maxWidth: '90vw', maxHeight: '90vh', minWidth: 320 }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between px-4 py-2.5 border-b border-slate-100 bg-slate-50/80">
                <div className="flex items-center gap-2 min-w-0">
                  <Icon size={14} className={color} />
                  <span className="text-[11px] font-medium text-slate-700 truncate">{att.basename}</span>
                  <span className="text-[9px] text-slate-400 uppercase flex-shrink-0">{att.CTYPE}</span>
                </div>
                <div className="flex items-center gap-1 flex-shrink-0 ml-3">
                  <a
                    href={att.URL}
                    download={att.basename}
                    className="p-1.5 rounded-md text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors"
                    title="İndir"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <FaDownload size={12} />
                  </a>
                  <a
                    href={att.URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="p-1.5 rounded-md text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors"
                    title="Yeni sekmede aç"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <FaExternalLinkAlt size={11} />
                  </a>
                  <button
                    onClick={() => setPreviewAttachment(null)}
                    className="p-1.5 rounded-md text-slate-400 hover:text-red-500 hover:bg-red-50 transition-colors ml-1"
                    title="Kapat"
                  >
                    <FaTimes size={13} />
                  </button>
                </div>
              </div>

              <div className="flex-1 flex items-center justify-center overflow-auto bg-slate-900/5 p-4" style={{ minHeight: 200 }}>
                {isImage && (
                  <img
                    src={att.URL}
                    alt={att.basename}
                    className="max-w-full max-h-[75vh] object-contain rounded"
                  />
                )}
                {isVideo && (
                  <video
                    src={att.URL}
                    controls
                    className="max-w-full max-h-[75vh] rounded"
                    style={{ outline: 'none' }}
                  />
                )}
                {isPdf && (
                  <iframe
                    src={att.URL}
                    title={att.basename}
                    className="w-full rounded border-0"
                    style={{ height: '75vh', minWidth: 500 }}
                  />
                )}
                {isSpreadsheet && (
                  <div className="w-full overflow-auto" style={{ maxHeight: '75vh', minWidth: 400 }}>
                    {spreadsheetLoading && (
                      <div className="flex items-center justify-center py-16 text-slate-400 text-[11px]">
                        Dosya yükleniyor...
                      </div>
                    )}
                    {spreadsheetData?.error && (
                      <div className="flex flex-col items-center gap-2 py-16 text-center">
                        <p className="text-[11px] text-red-400">Dosya okunamadı</p>
                        <a
                          href={att.URL}
                          download={att.basename}
                          className="flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-slate-700 text-white text-[11px] font-medium hover:bg-slate-800 transition-colors"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <FaDownload size={10} />
                          İndir
                        </a>
                      </div>
                    )}
                    {spreadsheetData && !spreadsheetData.error && (
                      <table className="min-w-full border-collapse text-[11px]" style={{ fontFamily: 'inherit' }}>
                        {spreadsheetData.headers.length > 0 && (
                          <thead className="sticky top-0 z-10">
                            <tr>
                              {spreadsheetData.headers.map((h, i) => (
                                <th
                                  key={i}
                                  className="px-3 py-2 text-left text-[10px] font-semibold text-slate-500 uppercase tracking-wider bg-slate-100 border-b border-slate-200 whitespace-nowrap"
                                >
                                  {h}
                                </th>
                              ))}
                            </tr>
                          </thead>
                        )}
                        <tbody>
                          {spreadsheetData.rows.slice(0, 200).map((row, ri) => (
                            <tr key={ri} className={`border-b border-slate-50 ${ri % 2 === 0 ? 'bg-white' : 'bg-slate-50/40'} hover:bg-blue-50/40`}>
                              {spreadsheetData.headers.map((_, ci) => (
                                <td
                                  key={ci}
                                  className="px-3 py-1.5 text-slate-600 whitespace-nowrap"
                                >
                                  {row[ci] !== undefined ? String(row[ci]) : ''}
                                </td>
                              ))}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                    {spreadsheetData && !spreadsheetData.error && spreadsheetData.rows.length > 200 && (
                      <div className="text-center py-2 text-[10px] text-slate-400 border-t border-slate-100">
                        İlk 200 satır gösteriliyor · Tamamını görmek için dosyayı indirin
                      </div>
                    )}
                  </div>
                )}
                {!isImage && !isVideo && !isPdf && !isSpreadsheet && (
                  <div className="flex flex-col items-center gap-3 py-10 text-center">
                    <div className="w-16 h-16 rounded-2xl bg-slate-100 flex items-center justify-center">
                      <Icon size={28} className={color} />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-slate-700">{att.basename}</p>
                      <p className="text-[10px] text-slate-400 mt-0.5 uppercase">{att.CTYPE} dosyası</p>
                    </div>
                    <div className="flex gap-2 mt-2">
                      <a
                        href={att.URL}
                        download={att.basename}
                        className="flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-slate-700 text-white text-[11px] font-medium hover:bg-slate-800 transition-colors"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <FaDownload size={10} />
                        İndir
                      </a>
                      <a
                        href={att.URL}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-slate-200 text-slate-600 text-[11px] font-medium hover:bg-slate-50 transition-colors"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <FaExternalLinkAlt size={9} />
                        Aç
                      </a>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
};

export default RightPanelBody;